/** Yaw (rad) about Z from geometry_msgs/Quaternion (map origin rotation). */
export function yawFromRosOrientation(ori: {
    x: number;
    y: number;
    z: number;
    w: number;
}): number {
    const { x, y, z, w } = ori;
    return Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
}

const DEFAULT_FILTER_RESOLUTION = 0.05;

/** Defaults written by `buildMapFilterYamlContent` (nav2 map_io trinary). */
export const NAV2_FILTER_MAP_IO_DEFAULTS = {
    negate: 0,
    freeThresh: 0.3,
    occupiedThresh: 0.65,
} as const;

export type Nav2TrinaryCellClass = "free" | "unknown" | "occupied";

/** Classify a `nav_msgs/OccupancyGrid` cell value (0–100, -1 unknown). */
export function nav2TrinaryClassFromOccupancyValue(value: number): Nav2TrinaryCellClass {
    if (!Number.isFinite(value) || value < 0) {
        return "unknown";
    }
    if (value === 0) {
        return "free";
    }
    if (value >= 65) {
        return "occupied";
    }
    if (value > 0) {
        return "unknown";
    }
    return "free";
}

/** Classify one grayscale map_io pixel (0–255) into free / unknown / occupied. */
export function nav2TrinaryClassFromGrayscalePixel(
    pixel0to255: number,
    opts: {
        negate?: number;
        freeThresh?: number;
        occupiedThresh?: number;
    } = {},
): Nav2TrinaryCellClass {
    const negate = opts.negate ?? NAV2_FILTER_MAP_IO_DEFAULTS.negate;
    const freeThresh = opts.freeThresh ?? NAV2_FILTER_MAP_IO_DEFAULTS.freeThresh;
    const occupiedThresh =
        opts.occupiedThresh ?? NAV2_FILTER_MAP_IO_DEFAULTS.occupiedThresh;
    const p = Math.max(0, Math.min(255, pixel0to255));
    const occ = negate ? p / 255 : (255 - p) / 255;
    if (occ < freeThresh) {
        return "free";
    }
    if (occ > occupiedThresh) {
        return "occupied";
    }
    return "unknown";
}

/** Classify RGB (grayscale mask) using average channel as map_io intensity. */
export function nav2TrinaryClassFromRgb(
    r: number,
    g: number,
    b: number,
    opts?: Parameters<typeof nav2TrinaryClassFromGrayscalePixel>[1],
): Nav2TrinaryCellClass {
    return nav2TrinaryClassFromGrayscalePixel((r + g + b) / 3, opts);
}

/** Map transform for filter YAML (from live `/map_metadata`). */
export type MapFilterGeometry = {
    resolution: number;
    originX: number;
    originY: number;
    originYaw: number;
    /** Live occupancy grid width in cells (materialize resamples variant mask to this size). */
    width?: number;
    /** Live occupancy grid height in cells. */
    height?: number;
};

export type MapMetadataForFilterGeometry = {
    width?: number;
    height?: number;
    resolution?: number;
    origin?: { x?: number; y?: number };
    originYaw?: number;
    /** `nav_msgs/OccupancyGrid.info.map_load_time` or `MapMetaData.map_load_time`. */
    mapLoadTimeSec?: number;
    mapLoadTimeNsec?: number;
    /** `OccupancyGrid.header.stamp` (publish time; not used for reload detection). */
    stampSec?: number;
    stampNsec?: number;
};

/** Combine ROS `sec` + `nanosec` into a single comparable timestamp (ns). */
export function rosTimeToNs(sec?: number, nsec?: number): number | null {
    if (typeof sec !== "number" || !Number.isFinite(sec)) {
        return null;
    }
    if (typeof nsec !== "number" || !Number.isFinite(nsec)) {
        return null;
    }
    return Math.trunc(sec) * 1_000_000_000 + Math.trunc(nsec);
}

/** `map_load_time` from parsed live metadata; null when missing. */
export function mapLoadTimeNsFromMetadata(
    meta: MapMetadataForFilterGeometry | null | undefined,
): number | null {
    return rosTimeToNs(meta?.mapLoadTimeSec, meta?.mapLoadTimeNsec);
}

/** Parsed live map metadata (`/map_metadata` or OccupancyGrid `info` for legacy messages). */
export type LiveMapMetadata = MapMetadataForFilterGeometry & {
    id?: string;
    name?: string;
    created_time?: number;
    desc?: string;
};

type RosOccupancyGridLike = {
    header?: {
        frame_id?: string;
        stamp?: { sec?: number; nanosec?: number };
    };
    info?: {
        map_load_time?: { sec?: number; nanosec?: number };
        resolution?: number;
        width?: number;
        height?: number;
        origin?: {
            position?: { x?: number; y?: number; z?: number };
            orientation?: { x?: number; y?: number; z?: number; w?: number };
        };
    };
};

type RosMapMetaDataLike = {
    map_load_time?: { sec?: number; nanosec?: number };
    resolution?: number;
    width?: number;
    height?: number;
    origin?: {
        position?: { x?: number; y?: number; z?: number };
        orientation?: { x?: number; y?: number; z?: number; w?: number };
    };
};

/** Parse Aurora `nav_msgs/MapMetaData` on `/map_metadata`; null if incomplete. */
export function parseMapMetaDataMessage(mapMetaDataMsg: unknown): LiveMapMetadata | null {
    const msg = mapMetaDataMsg as RosMapMetaDataLike;
    const width = msg?.width;
    const height = msg?.height;
    const resolution = msg?.resolution;
    const pos = msg?.origin?.position;
    const ori = msg?.origin?.orientation;
    if (
        typeof width !== "number" ||
        !Number.isFinite(width) ||
        width <= 0 ||
        typeof height !== "number" ||
        !Number.isFinite(height) ||
        height <= 0 ||
        typeof resolution !== "number" ||
        !Number.isFinite(resolution) ||
        resolution <= 0 ||
        typeof pos?.x !== "number" ||
        !Number.isFinite(pos.x) ||
        typeof pos?.y !== "number" ||
        !Number.isFinite(pos.y) ||
        !ori
    ) {
        return null;
    }
    const loadTime = msg.map_load_time;
    return {
        id: "aurora_map",
        name: "Aurora SLAM map",
        created_time: Date.now(),
        desc: "Live map from Aurora slamware (/map_metadata)",
        width,
        height,
        resolution,
        origin: { x: pos.x, y: pos.y },
        originYaw: yawFromRosOrientation(ori as { x: number; y: number; z: number; w: number }),
        ...(typeof loadTime?.sec === "number" && typeof loadTime?.nanosec === "number"
            ? { mapLoadTimeSec: loadTime.sec, mapLoadTimeNsec: loadTime.nanosec }
            : {}),
    };
}

/** Parse `/map_metadata` (MapMetaData) or OccupancyGrid `info` (legacy). */
export function parseLiveMapMetadataMessage(msg: unknown): LiveMapMetadata | null {
    return parseMapMetaDataMessage(msg) ?? parseOccupancyGridMetadataMessage(msg);
}

/** @deprecated Use {@link parseLiveMapMetadataMessage}. */
export const parseSlammapMetadataMessage = parseLiveMapMetadataMessage;

/** Parse a `/map_metadata` message into live map metadata; null if incomplete. */
export function parseOccupancyGridMetadataMessage(
    occupancyGridMsg: unknown,
): LiveMapMetadata | null {
    const msg = occupancyGridMsg as RosOccupancyGridLike;
    const info = msg?.info;
    const width = info?.width;
    const height = info?.height;
    const resolution = info?.resolution;
    const pos = info?.origin?.position;
    const ori = info?.origin?.orientation;
    if (
        typeof width !== "number" ||
        !Number.isFinite(width) ||
        width <= 0 ||
        typeof height !== "number" ||
        !Number.isFinite(height) ||
        height <= 0 ||
        typeof resolution !== "number" ||
        !Number.isFinite(resolution) ||
        resolution <= 0 ||
        typeof pos?.x !== "number" ||
        !Number.isFinite(pos.x) ||
        typeof pos?.y !== "number" ||
        !Number.isFinite(pos.y) ||
        !ori
    ) {
        return null;
    }
    const loadTime = info?.map_load_time;
    const headerStamp = msg.header?.stamp;
    return {
        id: msg.header?.frame_id || "current_map",
        name: "Current Map",
        created_time: Date.now(),
        desc: "Live map from Aurora/Nav2",
        width,
        height,
        resolution,
        origin: { x: pos.x, y: pos.y },
        originYaw: yawFromRosOrientation(ori as { x: number; y: number; z: number; w: number }),
        ...(typeof loadTime?.sec === "number" && typeof loadTime?.nanosec === "number"
            ? { mapLoadTimeSec: loadTime.sec, mapLoadTimeNsec: loadTime.nanosec }
            : {}),
        ...(typeof headerStamp?.sec === "number" && typeof headerStamp?.nanosec === "number"
            ? { stampSec: headerStamp.sec, stampNsec: headerStamp.nanosec }
            : {}),
    };
}

/** Extract filter geometry from live map metadata; undefined if incomplete. */
export function mapFilterGeometryFromMetadata(
    meta: MapMetadataForFilterGeometry | null | undefined,
): MapFilterGeometry | undefined {
    if (!meta) return undefined;
    const res = meta.resolution;
    if (typeof res !== "number" || !Number.isFinite(res) || res <= 0) return undefined;
    const ox = meta.origin?.x;
    const oy = meta.origin?.y;
    if (typeof ox !== "number" || !Number.isFinite(ox)) return undefined;
    if (typeof oy !== "number" || !Number.isFinite(oy)) return undefined;
    const yaw =
        typeof meta.originYaw === "number" && Number.isFinite(meta.originYaw)
            ? meta.originYaw
            : 0;
    const w = meta.width;
    const h = meta.height;
    const gridCells =
        typeof w === "number" &&
        Number.isFinite(w) &&
        w > 0 &&
        typeof h === "number" &&
        Number.isFinite(h) &&
        h > 0
            ? { width: Math.round(w), height: Math.round(h) }
            : {};
    return { resolution: res, originX: ox, originY: oy, originYaw: yaw, ...gridCells };
}

/** Aurora SDK publishes live grid geometry on this topic. */
export const MAP_METADATA_TOPIC = "/map_metadata";

/** Fingerprint for stable grid geometry (ignores header stamp). */
export function liveMapMetadataStabilityKey(
    meta: MapMetadataForFilterGeometry | null | undefined,
): string | null {
    if (!meta || !mapFilterGeometryFromMetadata(meta)) {
        return null;
    }
    const w = meta.width;
    const h = meta.height;
    if (typeof w !== "number" || typeof h !== "number" || w <= 0 || h <= 0) {
        return null;
    }
    const geo = mapFilterGeometryFromMetadata(meta)!;
    return `${w}x${h}@${geo.resolution}@${geo.originX},${geo.originY},${geo.originYaw}`;
}

/** Baseline fields for `/r2c2/clear_keepout_filter` after map clear / reinit. */
export function snapshotMapMetadataBaseline(
    meta: MapMetadataForFilterGeometry | null | undefined,
    metadataSeq = 0,
): {
    baseline_geometry_key: string;
    baseline_map_load_time_ns: number;
    baseline_metadata_seq: number;
} {
    return {
        baseline_geometry_key: liveMapMetadataStabilityKey(meta) ?? "",
        baseline_map_load_time_ns: mapLoadTimeNsFromMetadata(meta) ?? 0,
        baseline_metadata_seq: metadataSeq,
    };
}

export function appendMapFilterGeometryToFormData(
    form: FormData,
    geometry: MapFilterGeometry | undefined,
): void {
    if (!geometry) return;
    form.append("resolution", String(geometry.resolution));
    form.append("origin_x", String(geometry.originX));
    form.append("origin_y", String(geometry.originY));
    form.append("origin_yaw", String(geometry.originYaw));
}

export function mapFilterGeometryToJsonFields(
    geometry: MapFilterGeometry | undefined,
): Record<string, number> {
    if (!geometry) return {};
    const out: Record<string, number> = {
        resolution: geometry.resolution,
        origin_x: geometry.originX,
        origin_y: geometry.originY,
        origin_yaw: geometry.originYaw,
    };
    if (typeof geometry.width === "number" && geometry.width > 0) {
        out.width = Math.round(geometry.width);
    }
    if (typeof geometry.height === "number" && geometry.height > 0) {
        out.height = Math.round(geometry.height);
    }
    return out;
}

/**
 * Format floats from `/map_metadata` for YAML with near–full IEEE-754
 * precision (17 significant digits round-trips double; trim trailing zeros cosmetically).
 */
export function formatMapTopicFloatForYaml(n: number): string {
    if (!Number.isFinite(n)) return "0";
    if (Object.is(n, -0)) return "0";
    let s = n.toPrecision(17);
    if (/[eE]/.test(s)) {
        return s;
    }
    if (s.includes(".")) {
        s = s.replace(/\.?0+$/, "");
    }
    if (s === "" || s === "-") return "0";
    if (s === "-0") return "0";
    return s;
}

/** Third origin value (yaw, rad): use `0.0` for zero so YAML matches common map_server examples. */
function formatOriginYawForFilterYaml(n: number): string {
    if (n === 0 || Object.is(n, -0)) {
        return "0.0";
    }
    return formatMapTopicFloatForYaml(n);
}

/**
 * Initial map filter YAML (nav2 map_io–style). Prefer `/map_metadata` from Aurora;
 * if fields are missing, use neutral defaults. Set `imageAbsolutePath` when the
 * filter raster path is known (e.g. edited variant BMP on disk).
 */
export function buildMapFilterYamlContent(params: {
    resolution?: number;
    originX?: number;
    originY?: number;
    originYaw?: number;
    /** Live occupancy grid width (cells); written for mission preview alignment. */
    width?: number;
    /** Live occupancy grid height (cells). */
    height?: number;
    /** Robot filesystem path to the occupancy/filter BMP; empty string omits path in YAML. */
    imageAbsolutePath?: string;
}): string {
    const res =
        typeof params.resolution === "number" && Number.isFinite(params.resolution)
            ? params.resolution
            : DEFAULT_FILTER_RESOLUTION;
    const ox =
        typeof params.originX === "number" && Number.isFinite(params.originX)
            ? params.originX
            : 0;
    const oy =
        typeof params.originY === "number" && Number.isFinite(params.originY)
            ? params.originY
            : 0;
    const yaw =
        typeof params.originYaw === "number" && Number.isFinite(params.originYaw)
            ? params.originYaw
            : 0;
    const fmt = formatMapTopicFloatForYaml;
    const imgRaw = (params.imageAbsolutePath ?? "").trim().replace(/\\/g, "/");
    const imageScalar =
        imgRaw.length > 0 ? JSON.stringify(imgRaw) : '""';
    const lines = [
        `image: ${imageScalar}`,
        `resolution: ${fmt(res)}`,
        `origin: [${fmt(ox)}, ${fmt(oy)}, ${formatOriginYawForFilterYaml(yaw)}]`,
    ];
    if (typeof params.width === "number" && Number.isFinite(params.width) && params.width > 0) {
        lines.push(`width: ${Math.round(params.width)}`);
    }
    if (typeof params.height === "number" && Number.isFinite(params.height) && params.height > 0) {
        lines.push(`height: ${Math.round(params.height)}`);
    }
    return [
        ...lines,
        `negate: 0`,
        `occupied_thresh: 0.65`,
        `free_thresh: 0.3`,
        `mode: trinary`,
        "",
    ].join("\n");
}
